import type { UseFormReturn } from 'react-hook-form';
import { FormField, FormInput, FormSelect, SectionCard, StepHeader } from '../../../common';
import { type NumberPatternType, patternToRegex } from '@sbclite/core/schemas';
import type { RoutingRuleFormData } from '../schema';

/** Numbers tab: called/calling number pattern matching. */
export function NumbersTab({ form }: { form: UseFormReturn<RoutingRuleFormData> }) {
  const calledType = form.watch('numberPattern.called.type') || 'any';
  const callingType = form.watch('numberPattern.calling.type') || 'any';

  const patternTypeOptions: { value: NumberPatternType; label: string; description: string }[] = [
    { value: 'any', label: 'Any', description: 'Match all numbers' },
    { value: 'prefix', label: 'Starts with', description: 'Numbers starting with...' },
    { value: 'suffix', label: 'Ends with', description: 'Numbers ending with...' },
    { value: 'exact', label: 'Exact match', description: 'Exactly this number' },
    { value: 'contains', label: 'Contains', description: 'Numbers containing...' },
    { value: 'regex', label: 'Advanced (Regex)', description: 'Custom regex pattern' },
  ];

  const getPlaceholder = (type: NumberPatternType, isCalled: boolean) => {
    if (isCalled) {
      switch (type) {
        case 'prefix': return 'e.g., +1 or +46';
        case 'suffix': return 'e.g., @teams.microsoft.com';
        case 'exact': return 'e.g., +12025551234';
        case 'contains': return 'e.g., 555';
        case 'regex': return 'e.g., ^\\+1[2-9]\\d{9}$';
        default: return '';
      }
    } else {
      switch (type) {
        case 'prefix': return 'e.g., 1 (extensions starting with 1)';
        case 'suffix': return 'e.g., @internal.local';
        case 'exact': return 'e.g., 1001';
        case 'contains': return 'e.g., admin';
        case 'regex': return 'e.g., ^1[0-9]{3}$';
        default: return '';
      }
    }
  };

  return (
    <div className="space-y-6">
      <StepHeader
        title="Number Pattern Matching"
        description='Define which phone numbers this rule should match. Leave as "Any" to match all numbers.'
      />

      <div className="space-y-6">
        {/* Called Number (To/Request-URI) */}
        <SectionCard
          title="Called Number (Destination)"
          description="The number being called (To header / Request-URI)"
          variant="green"
          bordered
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="Match Type"
              hint={patternTypeOptions.find(o => o.value === calledType)?.description}
            >
              <FormSelect
                value={calledType}
                onChange={(e) => {
                  form.setValue('numberPattern.called.type', e.target.value as NumberPatternType);
                  if (e.target.value === 'any') {
                    form.setValue('numberPattern.called.value', '');
                  }
                }}
              >
                {patternTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </FormSelect>
            </FormField>

            {calledType !== 'any' && (
              <FormField
                label={calledType === 'regex' ? 'Regex Pattern' : 'Value'}
                hint={calledType === 'prefix' ? 'Example: "+1" matches all US numbers' :
                      calledType === 'suffix' ? 'Example: "@domain.com" matches SIP URIs to that domain' : undefined}
              >
                <FormInput
                  {...form.register('numberPattern.called.value')}
                  placeholder={getPlaceholder(calledType, true)}
                />
              </FormField>
            )}
          </div>

          {/* Preview of generated regex */}
          {calledType !== 'any' && form.watch('numberPattern.called.value') && (
            <div className="mt-3 p-2 bg-white rounded border text-xs">
              <span className="text-gray-500">Generated regex: </span>
              <code className="text-green-700">
                {patternToRegex(calledType, form.watch('numberPattern.called.value') || '')}
              </code>
            </div>
          )}
        </SectionCard>

        {/* Calling Number (From) */}
        <SectionCard
          title="Calling Number (Source)"
          description="The number making the call (From header)"
          variant="amber"
          bordered
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="Match Type"
              hint={patternTypeOptions.find(o => o.value === callingType)?.description}
            >
              <FormSelect
                value={callingType}
                onChange={(e) => {
                  form.setValue('numberPattern.calling.type', e.target.value as NumberPatternType);
                  if (e.target.value === 'any') {
                    form.setValue('numberPattern.calling.value', '');
                  }
                }}
              >
                {patternTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </FormSelect>
            </FormField>

            {callingType !== 'any' && (
              <FormField
                label={callingType === 'regex' ? 'Regex Pattern' : 'Value'}
                hint={callingType === 'prefix' ? 'Example: "1" matches extensions 1000-1999' : undefined}
              >
                <FormInput
                  {...form.register('numberPattern.calling.value')}
                  placeholder={getPlaceholder(callingType, false)}
                />
              </FormField>
            )}
          </div>

          {/* Preview of generated regex */}
          {callingType !== 'any' && form.watch('numberPattern.calling.value') && (
            <div className="mt-3 p-2 bg-white rounded border text-xs">
              <span className="text-gray-500">Generated regex: </span>
              <code className="text-amber-700">
                {patternToRegex(callingType, form.watch('numberPattern.calling.value') || '')}
              </code>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
